// 選択肢マスタ（内部コード ＋ 繁体字 zh-TW 表示値）。CLAUDE.md §6 / §14。
// 「その他」を各軸に逃がし弁として置く（運用で育てる）。

export interface Option {
  code: string;
  label: string;
}

export const CONTRACT_TYPES: Option[] = [
  { code: "service", label: "服務" },
  { code: "procurement", label: "採購" },
  { code: "license", label: "授權" },
  { code: "partnership", label: "合作" },
  { code: "other", label: "其他" },
];

// 継続性（フォーム出し分けの親フィールド）。
export const CONTINUITIES: Option[] = [
  { code: "oneoff", label: "單次" },
  { code: "continuous", label: "持續" },
  { code: "auto_renew", label: "自動續約" },
  { code: "other", label: "其他" },
];

export const CURRENCIES: Option[] = [
  { code: "NTD", label: "新台幣 (NT$)" },
  { code: "JPY", label: "日圓 (¥)" },
  { code: "USD", label: "美金 (US$)" },
];

export const AMOUNT_TYPES: Option[] = [
  { code: "annual", label: "年額" },
  { code: "total", label: "總額" },
];

export const SPECIAL_CLAUSES: Option[] = [
  { code: "penalty", label: "違約金" },
  { code: "exclusive", label: "獨家" },
  { code: "auto_renew", label: "自動續約" },
  { code: "min_order", label: "最低訂購量" },
  { code: "other", label: "其他" },
];

// 承認状態の表示と Badge 配色（二段階承認: 組長 → 處長）。
export const APPROVAL_STATUS: Record<string, { label: string; tone: "gray" | "amber" | "green" | "red" | "blue" }> = {
  draft: { label: "草稿", tone: "gray" },
  pending_team: { label: "待組長核准", tone: "amber" },
  pending_div: { label: "待處長核准", tone: "blue" },
  approved: { label: "已核准", tone: "green" },
  rejected: { label: "已退回", tone: "red" },
};

// ---- 評価欄マスタ（経営側が入力。§6-2 / §14 暫定） ----

// 損益影響。
export const PL_IMPACTS: Option[] = [
  { code: "cost_reduction", label: "成本削減" },
  { code: "cost_increase", label: "成本增加" },
  { code: "revenue_increase", label: "收益增加" },
  { code: "minor", label: "影響輕微" },
  { code: "other", label: "其他" },
];

// リスク評価（段階刻みは暫定）。
export const RISK_LEVELS: Option[] = [
  { code: "low", label: "低" },
  { code: "medium", label: "中" },
  { code: "high", label: "高" },
  { code: "none", label: "無特別風險" },
];

export const RISK_TONES: Record<string, "gray" | "amber" | "green" | "red"> = {
  low: "green",
  medium: "amber",
  high: "red",
  none: "gray",
};

// 分類 A / B / C（§5）。select には意味付きラベル、バッジには短ラベル。
export const CLASSIFICATIONS: Option[] = [
  { code: "A", label: "A類（無問題）" },
  { code: "B", label: "B類（台北內需確認）" },
  { code: "C", label: "C類（需總公司確認）" },
];

export const CLASSIFICATION_BADGE: Record<string, { label: string; tone: "gray" | "amber" | "green" | "red" | "blue" }> = {
  A: { label: "A類", tone: "green" },
  B: { label: "B類", tone: "amber" },
  C: { label: "C類", tone: "red" },
};

// 本社確認ステータス（C類のみ。§9-2 の3状態）。
export const HQ_REVIEW_STATUS: Option[] = [
  { code: "preparing", label: "準備委託中" },
  { code: "awaiting", label: "等待總公司回覆" },
  { code: "answered", label: "已有總公司回覆" },
];

export const HQ_REVIEW_TONES: Record<string, "gray" | "amber" | "green" | "red" | "blue"> = {
  preparing: "gray",
  awaiting: "amber",
  answered: "green",
};

// 進行段階（一直線。§4。二段階承認を含む）。
export const STAGE_LABELS: Record<string, string> = {
  draft: "草稿",
  pending_team_approval: "待組長核准",
  pending_div_approval: "待處長核准",
  returned: "已退回",
  reported: "已提報會議",
};

// 監査ログの操作コード → 繁体字ラベル（§11-2）。
export const AUDIT_ACTION_LABELS: Record<string, string> = {
  "contract.create.draft": "建立草稿",
  "contract.create.submit": "建立並送出",
  "contract.edit": "編輯內容",
  "contract.submit": "送出登記",
  "contract.approve": "核准",
  "contract.reject": "退回",
  "contract.evaluate": "評估／分類",
  "contract.view": "檢視合約",
  "contract.list.viewAll": "檢視全公司一覧",
  "comment.post.meeting_memo": "會議備忘留言",
  "comment.post.staff_contact": "與承辦人聯絡留言",
  "attachment.upload": "上傳附件",
  "attachment.download": "下載附件",
  "attachment.delete": "刪除附件",
};

// 監査ログの絞り込み用オプション（全部＋主要操作）。
export const AUDIT_ACTION_OPTIONS: Option[] = [
  { code: "", label: "全部操作" },
  { code: "contract.create.submit", label: "建立並送出" },
  { code: "contract.approve", label: "核准" },
  { code: "contract.reject", label: "退回" },
  { code: "contract.evaluate", label: "評估／分類" },
  { code: "contract.list.viewAll", label: "檢視全公司一覧" },
  { code: "contract.view", label: "檢視合約" },
];

// ロール表示。
export const ROLE_LABELS: Record<string, string> = {
  staff: "業務承辦人",
  team_lead: "組長",
  div_head: "處長",
  secretariat: "事務局",
  gm: "總經理",
};

// コード → 表示値の変換（見つからなければコードをそのまま返す）。
export function labelOf(options: Option[], code: string | null | undefined): string {
  if (!code) return "—";
  return options.find((o) => o.code === code)?.label ?? code;
}

// 複数コード（特殊条款）→ ラベル配列。
export function labelsOf(options: Option[], codes: string[]): string[] {
  return codes.map((c) => labelOf(options, c));
}
